import type { ActivityEstimate } from './types';

/** Burbank Analysis: non-CAP residential gas (not EIA-861). */
export const BURBANK_GAS_PHYSICAL_SERIES_LABEL = 'SoCal Gas (ClimatePlans)';

export type ActivityDataSourceKind =
  | 'eia_observed'
  | 'eia_interpolated'
  | 'eia_extrapolated'
  | 'cap_published'
  | 'cap_derived'
  | 'climateplans_legacy'
  | 'estimated';

export function classifyActivityDataSource(
  est: ActivityEstimate,
  options?: { treatUtilityAsEia?: boolean }
): ActivityDataSourceKind {
  const eia = options?.treatUtilityAsEia ?? false;
  if (est.coverage === 'cap_published') return 'cap_published';
  if (est.coverage === 'cap_derived_total') return 'cap_derived';
  if (est.sourceClass === 'cap_published' || est.sourceClass === 'cap_derived') {
    return est.sourceClass === 'cap_published' ? 'cap_published' : 'cap_derived';
  }
  if (eia && est.sourceClass === 'utility_observed') {
    if (est.coverage === 'interpolated') return 'eia_interpolated';
    if (est.coverage === 'extrapolated') return 'eia_extrapolated';
    return 'eia_observed';
  }
  if (est.coverage === 'interpolated') return 'estimated';
  if (est.coverage === 'extrapolated') return 'estimated';
  if (est.sourceClass === 'modeled' || est.sourceClass === 'hybrid') return 'climateplans_legacy';
  if (est.coverage === 'complete_observed' || est.coverage === 'partial_observed_lower_bound') {
    return eia ? 'eia_observed' : 'climateplans_legacy';
  }
  return 'estimated';
}

export function activityDataSourceLabel(kind: ActivityDataSourceKind): string {
  switch (kind) {
    case 'eia_observed':
      return 'EIA-861 observed';
    case 'eia_interpolated':
      return 'EIA-861 interpolated';
    case 'eia_extrapolated':
      return 'EIA-861 extrapolated (estimated)';
    case 'cap_published':
      return 'Published CAP';
    case 'cap_derived':
      return 'CAP-derived estimate';
    case 'climateplans_legacy':
      return 'ClimatePlans physical (comparison)';
    case 'estimated':
      return 'Modeled / estimated';
    default:
      return 'Unknown source';
  }
}

export function activityDataSourceLabelForEstimate(
  est: ActivityEstimate,
  options?: { treatUtilityAsEia?: boolean }
): string {
  if (
    est.methodId.startsWith('burbank_gas_res_socalgas_climateplans') ||
    est.methodId.startsWith('burbank_gas_res_climateplans_physical')
  ) {
    return BURBANK_GAS_PHYSICAL_SERIES_LABEL;
  }
  return activityDataSourceLabel(classifyActivityDataSource(est, options));
}

export function provenanceStackLabel(
  bucket: 'capAnchor' | 'utilityObserved' | 'interpolated' | 'extrapolated',
  options: {
    isBurbankEia: boolean;
    fuel: 'electricity' | 'natural_gas';
    isBhExperiment?: boolean;
    /** Beverly Hills commercial gas figure (SoCalGas commercial category). */
    isBhCommercialGasChart?: boolean;
  }
): string {
  if (options.isBhExperiment && options.isBhCommercialGasChart && options.fuel === 'natural_gas') {
    if (bucket === 'capAnchor') return 'CAP-derived anchor (2015 / 2019)';
    if (bucket === 'utilityObserved') return 'SoCalGas commercial (observed)';
    if (bucket === 'interpolated') return 'Modeled interpolation / bridge';
    return 'Modeled backcast (ClimatePlans shape)';
  }
  if (options.isBhExperiment && options.fuel === 'natural_gas') {
    if (bucket === 'capAnchor') return 'CAP anchor year (residential estimate)';
    if (bucket === 'utilityObserved') return 'SoCalGas residential (observed)';
    if (bucket === 'interpolated') return 'Modeled interpolation / bridge';
    return 'Modeled backcast (ClimatePlans shape)';
  }
  if (bucket === 'capAnchor' && options.isBurbankEia) {
    return 'Published CAP (Rincon 2024)';
  }
  if (options.isBurbankEia && options.fuel === 'electricity') {
    if (bucket === 'utilityObserved') return 'EIA-861 observed';
    if (bucket === 'interpolated') return 'EIA-861 interpolated';
    if (bucket === 'extrapolated') return 'EIA-861 extrapolated (estimated)';
    return 'Published CAP (Rincon 2024)';
  }
  if (options.isBurbankEia && options.fuel === 'natural_gas') {
    if (bucket === 'capAnchor') return 'Published CAP (Rincon 2024)';
    if (bucket === 'utilityObserved') return BURBANK_GAS_PHYSICAL_SERIES_LABEL;
    if (bucket === 'interpolated') return 'CAP linear interpolation';
    if (bucket === 'extrapolated') return 'CAP flat extension';
    return 'Published CAP (Rincon 2024)';
  }
  if (bucket === 'capAnchor') return 'CAP published / CAP-derived anchor';
  if (bucket === 'utilityObserved') return 'Utility observed';
  if (bucket === 'interpolated') return 'Interpolation';
  return 'Model-shaped extrapolation';
}
